import { CONCEPT_ICONS, Icons, PocketRow } from "@/components/ui";
import { useT } from "@/i18n";
import { settingsBrowserMessages } from "@/i18n/messages/settings-browser";
import { BROWSERS, type BrowserId } from "@/desktop/browser-connection";
import type {
  BrowserExtension,
  BrowserExtensionStates,
} from "@/desktop/denker-browser";
import {
  ConnectionChip,
  ConnectionMessage,
  ConnectionStatus,
  type ConnectionTone,
} from "./browser-connection-chip";
import { useAppleEventsConnection } from "./use-apple-events-connection";
import { useExtensionConnection } from "./use-extension-connection";

/**
 * A Chromium browser's row (Chrome, Dia, Brave, Arc, Edge): its status is
 * the Denker extension's alone - Apple Events never make it "Connected" -
 * and each connection is a chip at its end: the extension's wherever its
 * state is known, Apple Events' where they apply (macOS).
 */
export function ChromiumBrowserRow({
  browserId,
  extension,
  loaded,
  refresh,
}: {
  browserId: BrowserId;
  extension: BrowserExtension | undefined;
  /** The extension's state was read, so `extension` undefined is no row. */
  loaded: boolean;
  refresh: () => Promise<BrowserExtensionStates | null>;
}) {
  const t = useT(settingsBrowserMessages);
  const ext = useExtensionConnection(browserId, extension, loaded, refresh);
  const apple = useAppleEventsConnection(browserId);
  const tone: ConnectionTone =
    ext.phase === "working"
      ? "idle"
      : extension?.state === "connected"
        ? "connected"
        : extension?.state === "disabled" ||
            extension?.state === "turned_off" ||
            extension?.state === "consent" ||
            extension?.state === "outdated"
          ? "attention"
          : "idle";
  const messages = [ext.message, apple.applies ? apple.message : null].filter(
    (message) => message !== null,
  );
  return (
    <PocketRow
      testId={`connections-${browserId}`}
      title={BROWSERS[browserId].name}
      leading={<CONCEPT_ICONS.app className="h-4 w-4 text-muted" />}
      secondLine={
        <ConnectionStatus
          testId={`browser-extension-${browserId}`}
          tone={tone}
          label={ext.label}
        />
      }
      trailing={
        <>
          {extension ? (
            <ConnectionChip
              testId={`browser-extension-chip-${browserId}`}
              icon={<Icons.Puzzle />}
              name={t("chipExtension")}
              phase={ext.phase}
              does={ext.does}
              state={t("statusConnected")}
              onActivate={ext.activate}
              onReconnect={ext.reconnect}
              onDisconnect={ext.disconnect}
            />
          ) : null}
          {apple.applies ? (
            <ConnectionChip
              testId={`browser-apple-events-chip-${browserId}`}
              icon={<Icons.Command />}
              name={t("chipAppleEvents")}
              phase={apple.phase}
              does={apple.does}
              state={
                apple.connected
                  ? t("statusConnected")
                  : (apple.verified ?? apple.label)
              }
              onActivate={apple.activate}
              onReconnect={() => apple.run("check")}
              onDisconnect={() => apple.run("disconnect")}
            />
          ) : null}
        </>
      }
      below={
        messages.length
          ? messages.map((message, at) => (
              <ConnectionMessage key={at} message={message} />
            ))
          : undefined
      }
    />
  );
}
